// Agenda > Estoque — backend real (2026-09-21,
// `/opt/gpsdrive/gpsdrive-backend-release-fase1-2/src/stock/`). Itens
// avulsos + catálogo de modelos (com foto) vêm daqui; rastreadores já
// instalados em clientes vêm da frota real (GET /api/v1/vehicles).
import {
  gpsDriveGet,
  gpsDrivePost,
  gpsDrivePut,
  gpsDriveUpload,
} from "@/lib/api/gpsdrive-client";

// "com_tecnico": na rua num atendimento externo — só muda pelo atendimento.
export type StockStatus = "disponivel" | "manutencao" | "defeito" | "teste" | "com_tecnico";

export interface TrackerModel {
  id: string;
  nome: string;
  imageKey: string | null;
}

export interface StockItem {
  id: string;
  imei: string;
  modelo: string;
  fornecedor: string | null;
  garantia: string | null;
  chipNumero: string | null;
  chipIccid: string | null;
  chipOperadora: string | null;
  localizacao: string | null;
  status: StockStatus;
  createdAt: string;
}

export interface CreateStockItemsInput {
  imeis: string[];
  chips?: Array<{ numero?: string | null; iccid?: string | null }>;
  modelo: string;
  fornecedor?: string | null;
  garantia?: string | null;
  chipOperadora?: string | null;
  localizacao?: string | null;
  status?: StockStatus;
}

export type UpdateStockItemInput = Partial<
  Omit<StockItem, "id" | "createdAt" | "status"> & { status: StockStatus }
>;

export type MovementAction =
  | "descartar"
  | "voltar_estoque"
  | "testar"
  // saída/retorno com técnico (agenda-equipment no backend)
  | "saida_tecnico"
  | "saida_cancelada"
  | "instalado"
  | "devolvido"
  | "defeito";

export interface StockMovement {
  id: string;
  imei: string;
  modelo: string;
  acao: MovementAction;
  motivo: string;
  createdAt: string;
}

export interface MoveTrackerInput {
  imei: string;
  acao: MovementAction;
  motivo: string;
  modelo: string;
  fornecedor?: string | null;
  garantia?: string | null;
  chipNumero?: string | null;
  chipIccid?: string | null;
  chipOperadora?: string | null;
  localizacao?: string | null;
}

// Rastreador já instalado num veículo da frota (só leitura no Estoque).
export interface FleetTracker {
  deviceId: number;
  imei: string;
  modelo: string | null;
  fornecedor: string | null;
  chipNumero: string | null;
  chipIccid: string | null;
  chipOperadora: string | null;
  garantia: string | null;
  veiculo: string;
  instaladoEm: string | null;
}

interface FleetProfile {
  id: number;
  tracking: { name: string; plate: string };
  vehicle?: { name?: string | null };
  tracker: {
    imei: string;
    iccid: string | null;
    simNumber: string | null;
    carrier: string | null;
    supplier: string | null;
    model: string | null;
  };
  installation?: { date: string | null; warranty: string | null };
}

export const gpsDriveStockService = {
  models: (): Promise<TrackerModel[]> => gpsDriveGet("/api/v1/stock/models"),
  createModel: (input: { nome: string; imageKey?: string | null }): Promise<TrackerModel> =>
    gpsDrivePost("/api/v1/stock/models", input),
  updateModel: (
    id: string,
    input: { nome?: string; imageKey?: string | null },
  ): Promise<TrackerModel> => gpsDrivePut(`/api/v1/stock/models/${id}`, input),
  uploadImage: (file: File): Promise<{ key: string }> =>
    gpsDriveUpload("/api/v1/storage/upload", file),

  items: (): Promise<StockItem[]> => gpsDriveGet("/api/v1/stock/items"),
  createItems: (input: CreateStockItemsInput): Promise<StockItem[]> =>
    gpsDrivePost("/api/v1/stock/items", input),
  updateItem: (id: string, input: UpdateStockItemInput): Promise<StockItem> =>
    gpsDrivePut(`/api/v1/stock/items/${id}`, input),
  discardItem: (id: string): Promise<void> => gpsDrivePost(`/api/v1/stock/items/${id}/discard`),

  movements: (): Promise<StockMovement[]> => gpsDriveGet("/api/v1/stock/movements"),
  moveTracker: (input: MoveTrackerInput): Promise<void> =>
    gpsDrivePost("/api/v1/stock/movements", input),

  fleetTrackers: (): Promise<FleetTracker[]> =>
    gpsDriveGet<FleetProfile[]>("/api/v1/vehicles").then((rows) =>
      rows.map((r) => ({
        deviceId: r.id,
        imei: r.tracker.imei,
        modelo: r.tracker.model,
        fornecedor: r.tracker.supplier,
        chipNumero: r.tracker.simNumber,
        chipIccid: r.tracker.iccid,
        chipOperadora: r.tracker.carrier,
        garantia: r.installation?.warranty ?? null,
        veiculo: [r.vehicle?.name || r.tracking.name, r.tracking.plate].filter(Boolean).join(" · "),
        instaladoEm: r.installation?.date ?? null,
      })),
    ),
};
